import React, { useCallback, useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, UploadFile as UploadFileIcon } from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import PDFViewer from '../../components/shared/PDFViewer';
import InformePersonalizadoParametrosDialog from '../../components/ventas/InformePersonalizadoParametrosDialog';
import informesBibliotecaAdminService, {
  InformeBibliotecaDetail,
  InformeBibliotecaInstalacion,
  InformeBibliotecaVersion,
} from '../../services/informesBibliotecaAdminService';
import { INFORMES_MODULOS } from '../../config/informesModulos';
import type { InformeParametro } from '../../services/informesStoreService';
import { getApiErrorMessage } from '../../utils/apiError';

const tipoLabel = (tipo: string) => {
  switch (tipo) {
    case 'Patch':
      return 'Corrección';
    case 'Compatible':
      return 'Mejora compatible';
    case 'Breaking':
      return 'Cambio incompatible';
    default:
      return tipo;
  }
};

const estadoLabel = (estado: string) => {
  switch (estado) {
    case 'Draft':
      return 'Borrador';
    case 'Published':
      return 'Publicada';
    case 'Deprecated':
      return 'Deprecada';
    default:
      return estado;
  }
};

const BibliotecaInformeDetalle: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const informeId = Number(id);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [detail, setDetail] = useState<InformeBibliotecaDetail | null>(null);
  const [instalaciones, setInstalaciones] = useState<InformeBibliotecaInstalacion[]>([]);

  const [titulo, setTitulo] = useState('');
  const [descripcion, setDescripcion] = useState('');
  const [modulo, setModulo] = useState('Ventas');
  const [planRequerido, setPlanRequerido] = useState('Free');
  const [activo, setActivo] = useState(true);

  const [uploadOpen, setUploadOpen] = useState(false);
  const [archivo, setArchivo] = useState<File | null>(null);
  const [tipoCambio, setTipoCambio] = useState('Patch');
  const [notas, setNotas] = useState('');

  const [paramDialogOpen, setParamDialogOpen] = useState(false);
  const [paramVersion, setParamVersion] = useState<InformeBibliotecaVersion | null>(null);
  const [parametros, setParametros] = useState<InformeParametro[]>([]);
  const [rendering, setRendering] = useState(false);
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfTitle, setPdfTitle] = useState('');

  const load = useCallback(async () => {
    if (!Number.isFinite(informeId) || informeId <= 0) return;
    setLoading(true);
    try {
      const data = await informesBibliotecaAdminService.getById(informeId);
      setDetail(data);
      setTitulo(data.titulo);
      setDescripcion(data.descripcion || '');
      setModulo(data.modulo);
      setPlanRequerido(data.planRequerido);
      setActivo(data.activo);
      const inst = await informesBibliotecaAdminService.listInstalaciones(informeId);
      setInstalaciones(inst);
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo cargar el informe.'), { variant: 'error' });
      setDetail(null);
    } finally {
      setLoading(false);
    }
  }, [enqueueSnackbar, informeId]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    return () => {
      if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    };
  }, [pdfUrl]);

  if (!Number.isFinite(informeId) || informeId <= 0) {
    return <Navigate to="/sistema/biblioteca-informes" replace />;
  }

  const handleSaveMeta = async () => {
    if (!titulo.trim()) {
      enqueueSnackbar('El título es obligatorio.', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      const updated = await informesBibliotecaAdminService.update(informeId, {
        titulo: titulo.trim(),
        descripcion: descripcion.trim() || null,
        modulo: modulo.trim() || 'Ventas',
        planRequerido: planRequerido.trim() || 'Free',
        activo,
      });
      setDetail(updated);
      enqueueSnackbar('Metadatos actualizados.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo guardar.'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handleUpload = async () => {
    if (!archivo) {
      enqueueSnackbar('Seleccioná un archivo .rdl o .rdlc.', { variant: 'warning' });
      return;
    }
    setSaving(true);
    try {
      await informesBibliotecaAdminService.uploadVersion(informeId, archivo, tipoCambio, notas.trim() || undefined);
      enqueueSnackbar('Versión cargada como borrador.', { variant: 'success' });
      setUploadOpen(false);
      setArchivo(null);
      setNotas('');
      setTipoCambio('Patch');
      await load();
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo subir la versión.'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handlePublish = async (version: InformeBibliotecaVersion) => {
    const promocionar =
      version.tipoCambio === 'Compatible'
        ? window.confirm('¿Promocionar esta versión como la publicada para quienes no tienen pin?')
        : version.tipoCambio === 'Patch';
    setSaving(true);
    try {
      await informesBibliotecaAdminService.publish(informeId, version.id, promocionar);
      enqueueSnackbar('Versión publicada.', { variant: 'success' });
      await load();
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo publicar la versión.'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const handlePromote = async (version: InformeBibliotecaVersion) => {
    if (!window.confirm(`¿Hacer de la v${version.numero} la versión publicada?`)) return;
    setSaving(true);
    try {
      await informesBibliotecaAdminService.promote(informeId, version.id);
      enqueueSnackbar('Versión promocionada.', { variant: 'success' });
      await load();
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo promocionar la versión.'), { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const showPdf = (blob: Blob, title: string) => {
    const url = URL.createObjectURL(blob);
    setPdfUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
    setPdfTitle(title);
  };

  const executePreview = async (version: InformeBibliotecaVersion, params?: Record<string, unknown>) => {
    setRendering(true);
    try {
      const blob = await informesBibliotecaAdminService.previewPdf(informeId, version.id, params);
      const safeBlob = blob.type === 'application/pdf' ? blob : new Blob([blob], { type: 'application/pdf' });
      showPdf(safeBlob, `${detail?.titulo || 'Informe'} v${version.numero}`);
      setParamDialogOpen(false);
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo generar el PDF.'), { variant: 'error' });
    } finally {
      setRendering(false);
    }
  };

  const handlePreview = async (version: InformeBibliotecaVersion) => {
    if (!version.parametros?.length) {
      await executePreview(version);
      return;
    }
    setParamVersion(version);
    setParametros(version.parametros);
    setParamDialogOpen(true);
  };

  const handlePin = async (row: InformeBibliotecaInstalacion, versionId: number | null) => {
    try {
      const updated = await informesBibliotecaAdminService.setPin(informeId, row.tenantId, versionId);
      setInstalaciones((prev) => prev.map((item) => (item.tenantId === row.tenantId ? updated : item)));
      enqueueSnackbar(versionId ? 'Versión fijada.' : 'Pin quitado.', { variant: 'success' });
    } catch (err) {
      enqueueSnackbar(getApiErrorMessage(err, 'No se pudo actualizar el pin.'), { variant: 'error' });
    }
  };

  const publishedVersions = (detail?.versiones || []).filter((v) => v.estado === 'Published');

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Stack spacing={2}>
        <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Stack spacing={2}>
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                flexWrap="wrap"
                useFlexGap
                sx={{ position: 'relative', zIndex: 2 }}
              >
                <Button
                  variant="outlined"
                  startIcon={<ArrowBackIcon />}
                  onClick={() => navigate('/sistema/biblioteca-informes')}
                  sx={{
                    borderRadius: 2,
                    px: 2.5,
                    py: 1,
                    textTransform: 'none',
                    fontWeight: 500,
                    flexShrink: 0,
                    position: 'relative',
                    zIndex: 2,
                  }}
                >
                  Volver
                </Button>
                {detail && (
                  <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                    {detail.codigo}
                  </Typography>
                )}
              </Stack>
              {loading || !detail ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
                  <CircularProgress />
                </Box>
              ) : (
                <>
                  <Stack spacing={2} sx={{ maxWidth: 640 }}>
                    <TextField
                      label="Título"
                      value={titulo}
                      onChange={(e) => setTitulo(e.target.value)}
                      fullWidth
                      required
                      inputProps={{ maxLength: 120 }}
                    />
                    <FormControl fullWidth>
                      <InputLabel id="informe-modulo">Módulo</InputLabel>
                      <Select
                        labelId="informe-modulo"
                        label="Módulo"
                        value={modulo}
                        onChange={(e) => setModulo(e.target.value)}
                      >
                        {INFORMES_MODULOS.map((item) => (
                          <MenuItem key={item.modulo} value={item.modulo}>
                            {item.label}
                          </MenuItem>
                        ))}
                        {modulo && !INFORMES_MODULOS.some((item) => item.modulo === modulo) && (
                          <MenuItem value={modulo}>{modulo}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <FormControl fullWidth>
                      <InputLabel id="informe-plan">Plan</InputLabel>
                      <Select
                        labelId="informe-plan"
                        label="Plan"
                        value={planRequerido}
                        onChange={(e) => setPlanRequerido(e.target.value)}
                      >
                        <MenuItem value="Free">Free</MenuItem>
                        <MenuItem value="Premium">Premium</MenuItem>
                        {planRequerido && planRequerido !== 'Free' && planRequerido !== 'Premium' && (
                          <MenuItem value={planRequerido}>{planRequerido}</MenuItem>
                        )}
                      </Select>
                    </FormControl>
                    <TextField
                      label="Descripción"
                      value={descripcion}
                      onChange={(e) => setDescripcion(e.target.value.slice(0, 200))}
                      fullWidth
                      multiline
                      minRows={2}
                      helperText={`${descripcion.length}/200`}
                    />
                    <FormControlLabel
                      control={<Switch checked={activo} onChange={(e) => setActivo(e.target.checked)} />}
                      label="Activo en el store"
                    />
                    <Box>
                      <Button variant="contained" onClick={() => void handleSaveMeta()} disabled={saving}>
                        Guardar metadatos
                      </Button>
                    </Box>
                  </Stack>
                </>
              )}
            </Stack>
          </CardContent>
        </Card>

        {detail && (
          <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack spacing={2}>
                <Stack direction={{ xs: 'column', sm: 'row' }} justifyContent="space-between" spacing={1.5}>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Versiones
                  </Typography>
                  <Button variant="outlined" startIcon={<UploadFileIcon />} onClick={() => setUploadOpen(true)}>
                    Subir versión
                  </Button>
                </Stack>
                {detail.versiones.length === 0 ? (
                  <Typography color="text.secondary">Todavía no hay versiones. Subí un .rdlc para publicarlo.</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>N°</TableCell>
                          <TableCell>Tipo</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell>Archivo</TableCell>
                          <TableCell>Notas</TableCell>
                          <TableCell align="right">Acciones</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {detail.versiones.map((version) => (
                          <TableRow key={version.id}>
                            <TableCell>
                              v{version.numero}
                              {version.esPublicada && (
                                <Chip size="small" label="Publicada" color="primary" sx={{ ml: 1 }} />
                              )}
                            </TableCell>
                            <TableCell>{tipoLabel(version.tipoCambio)}</TableCell>
                            <TableCell>{estadoLabel(version.estado)}</TableCell>
                            <TableCell>{version.nombreArchivoOriginal}</TableCell>
                            <TableCell sx={{ maxWidth: 220 }}>{version.notas || '—'}</TableCell>
                            <TableCell align="right">
                              <Stack direction="row" spacing={1} justifyContent="flex-end">
                                <Button size="small" onClick={() => void handlePreview(version)} disabled={rendering}>
                                  Preview
                                </Button>
                                {version.estado === 'Draft' && (
                                  <Button size="small" variant="contained" onClick={() => void handlePublish(version)} disabled={saving}>
                                    Publicar
                                  </Button>
                                )}
                                {version.estado === 'Published' && !version.esPublicada && (
                                  <Button size="small" onClick={() => void handlePromote(version)} disabled={saving}>
                                    Promocionar
                                  </Button>
                                )}
                              </Stack>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}

        {detail && (
          <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
            <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
              <Stack spacing={2}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Instalaciones
                </Typography>
                {instalaciones.length === 0 ? (
                  <Typography color="text.secondary">Ningún tenant instaló este informe todavía.</Typography>
                ) : (
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Tenant</TableCell>
                          <TableCell>Estado</TableCell>
                          <TableCell>Versión fijada</TableCell>
                          <TableCell>Usuario</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {instalaciones.map((row) => (
                          <TableRow key={row.tenantId}>
                            <TableCell>
                              {row.tenantCode} — {row.tenantName}
                            </TableCell>
                            <TableCell>{row.instalado ? 'Instalado' : 'Quitado'}</TableCell>
                            <TableCell>
                              {row.instalado ? (
                                <FormControl size="small" sx={{ minWidth: 180 }}>
                                  <InputLabel>Pin</InputLabel>
                                  <Select
                                    label="Pin"
                                    value={row.versionFijadaId == null ? 'follow' : String(row.versionFijadaId)}
                                    onChange={(e) => {
                                      const value = String(e.target.value);
                                      void handlePin(row, value === 'follow' ? null : Number(value));
                                    }}
                                  >
                                    <MenuItem value="follow">Sigue la publicada</MenuItem>
                                    {publishedVersions.map((version) => (
                                      <MenuItem key={version.id} value={String(version.id)}>
                                        v{version.numero}
                                      </MenuItem>
                                    ))}
                                  </Select>
                                </FormControl>
                              ) : (
                                '—'
                              )}
                            </TableCell>
                            <TableCell>{row.usuarioPick || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Stack>
            </CardContent>
          </Card>
        )}
      </Stack>

      <Dialog open={uploadOpen} onClose={saving ? undefined : () => setUploadOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle>Subir versión</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Tipo de cambio</InputLabel>
              <Select
                label="Tipo de cambio"
                value={tipoCambio}
                onChange={(e) => setTipoCambio(String(e.target.value))}
              >
                <MenuItem value="Patch">Corrección (se promociona al publicar)</MenuItem>
                <MenuItem value="Compatible">Mejora compatible</MenuItem>
                <MenuItem value="Breaking">Cambio incompatible</MenuItem>
              </Select>
            </FormControl>
            <TextField
              label="Notas"
              value={notas}
              onChange={(e) => setNotas(e.target.value.slice(0, 500))}
              fullWidth
              multiline
              minRows={2}
            />
            <Button variant="outlined" component="label" startIcon={<UploadFileIcon />} disabled={saving}>
              {archivo ? archivo.name : 'Seleccionar archivo .rdl / .rdlc'}
              <input
                type="file"
                accept=".rdl,.rdlc,application/xml,text/xml"
                hidden
                onChange={(e) => setArchivo(e.target.files?.[0] || null)}
              />
            </Button>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)} disabled={saving}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={() => void handleUpload()} disabled={saving}>
            {saving ? 'Subiendo…' : 'Subir borrador'}
          </Button>
        </DialogActions>
      </Dialog>

      <InformePersonalizadoParametrosDialog
        open={paramDialogOpen}
        titulo={paramVersion ? `Preview v${paramVersion.numero}` : ''}
        parametros={parametros}
        loading={rendering}
        onClose={() => {
          if (!rendering) setParamDialogOpen(false);
        }}
        onConfirm={(values) => {
          if (!paramVersion) return;
          void executePreview(paramVersion, values);
        }}
      />

      {pdfUrl && (
        <PDFViewer
          pdfUrl={pdfUrl}
          onClose={() => {
            setPdfUrl((prev) => {
              if (prev) URL.revokeObjectURL(prev);
              return null;
            });
          }}
          title={pdfTitle}
          documentId={String(informeId)}
          documentType="informeCliente"
          downloadFileName={`${pdfTitle || 'informe'}.pdf`}
        />
      )}
    </Box>
  );
};

export default BibliotecaInformeDetalle;
